import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { TripResponse } from "../../types/api";
import { ColDef, GridApi } from "ag-grid-community/";
import { useTranslation } from "react-i18next";
import StatusCellRenderer from "./columnDefComponents/StatusCellRenderer";
import DateCellRenderer from "./columnDefComponents/DateCellRenderer";
import CityNameCellRenderer from "./columnDefComponents/CityNameCellRenderer";
import ApprovalCellRenderer from "./columnDefComponents/ApprovalCellRenderer";
import DocumentsCellRenderer from "./columnDefComponents/DocumentsCellRenderer";
import { agGridStyle } from "./style";
import TractorLicensePlateCellRenderer from "./columnDefComponents/TractorLicensePlateCellRenderer";
import PaginationSizeSelector from "./columnDefComponents/PaginationSizeSelector";
import { TripIdCellRenderer } from "./columnDefComponents/TripIdCellRenderer";
import { useTripContext } from "../../context/tripsContext";
import { useNavigate } from "react-router-dom";
import { useBusinessProfile } from "../../utils/hooks/useBusinessProfile";
import { useAuth } from "../../utils/hooks/useAuth";
import { StatusOptions } from "../../types/FilterEnums";
import { CircularProgress, Box } from "@mui/material";
import ApprovalFilter from "./columnDefComponents/ApprovalFilter";
import StatusFilter from "./columnDefComponents/StatusFilter";
import TractorPlateFilter from "./columnDefComponents/TractorPlateFilter";
import TrailerPlateFilter from "./columnDefComponents/TrailerPlateFilter";

interface AgGridTripsProps {
  trips: TripResponse[];
  vehiclePlateList: any[];
  trailerPlateList: any[];
  driversMap: Record<string, boolean>;
  isLoadingDrivers: boolean;
  isLoading?: boolean;
}

const AgGridTrips: React.FC<AgGridTripsProps> = ({ trips, vehiclePlateList, trailerPlateList, driversMap, isLoadingDrivers, isLoading = false }) => {
  const { t } = useTranslation();
  const { setTripId } = useTripContext();
  const { businessProfile } = useBusinessProfile();
  const gridRef = useRef<any>(null);
  const [paginationPageSize, setPaginationPageSize] = useState(50);
  const navigate = useNavigate();
  const [localTrips, setLocalTrips] = useState(trips);

  // Update localTrips when trips prop changes
  useEffect(() => {
    setLocalTrips(trips);
  }, [trips]);

  const updateRowApprovalStatus = (tmsKey: any, newStatus: string) => {
    // Trova la riga corrispondente nei dati locali
    const updatedTrips = localTrips.map((trip) =>
      trip.tmsObjectNumber === tmsKey
        ? { ...trip, approval: newStatus } // Aggiorna lo status
        : trip
    );

    // Aggiorna lo stato
    setLocalTrips(updatedTrips);
  };

  const { user } = useAuth();

  const onGridReady = (params: { api: GridApi }) => {
    if (gridRef.current) {
      // Initialize the approval filter
      params.api.getFilterInstance('approval', (instance) => {
        if (instance) {
          instance.setModel(null);
        }
      });

      // Initialize the tractor plate filter
      params.api.getFilterInstance('tractorLicensePlate', (instance) => {
        if (instance) {
          instance.setModel(null);
        }
      });

      // Initialize the trailer plate filter
      params.api.getFilterInstance('trailerLicensePlate', (instance) => {
        if (instance) {
          instance.setModel(null);
        }
      });
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Trip ID",
        field: "tmsObjectNumber",
        minWidth: 140,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          const tripId = params.data.tripID;
          const tmsObjectNumber = params.value;

          const handleClick = (id: number) => {
            navigate(`/tripdetails/${id}`);
          };

          return (
            <div style={agGridStyle.wrapperCell}>
              <TripIdCellRenderer
                displayValue={tmsObjectNumber}
                onClick={() => handleClick(tripId)}
              />
            </div>
          );
        },
      },
      {
        headerName: t("screens.trips.columnDef.status"),
        field: "status",
        minWidth: 220,
        filter: 'StatusFilter',
        filterParams: {
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        valueGetter: (params: {
          data?: {
            tmsObjectNumber: string; status?: string
          }
        }) =>
          statusMap[params.data?.status || ""] || params.data?.status,
        cellRenderer: (params: any) => {
          const translatedStatus = statusMap[params.value as string] || params.value;
          return (
            <div style={agGridStyle.wrapperCellStatus}>
              <StatusCellRenderer value={reverseStatusMap[translatedStatus] as StatusOptions} />
            </div>
          );
        },
      },
      {
        headerName: t("screens.trips.columnDef.approval"),
        field: "approval",
        minWidth: 200,
        filter: 'agCustomFilter',
        filterParams: {
          filter: ApprovalFilter,
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        valueGetter: (params: {
          data?: {
            tmsObjectNumber: string; approval?: string
          }
        }) =>
          approvalMap[params.data?.approval || ""] || params.data?.approval,
        cellRenderer: (params: any) => (
          <div style={agGridStyle.wrapperCell}>
            <ApprovalCellRenderer
              tmsKey={params.data?.tmsObjectNumber}
              tripId={params.data?.tripID}
              value={reverseApprovalMap[approvalMap[params.value as string] || params.value]}
              tripAcceptance={null}
              status={params.data?.status}
              onUpdate={updateRowApprovalStatus}
              gridRef={gridRef}
            />
          </div>
        )
      },
      {
        headerName: t("screens.trips.columnDef.loadDate"),
        field: "expectedDeparture",
        minWidth: 180,
        cellRenderer: (params: any) => {
          const cellValue = params.data?.expectedDeparture;
          if (!cellValue) return null;
          const parsedDate = new Date(cellValue);
          const utcDate = new Date(parsedDate.getTime())
          return (
            <div style={agGridStyle.wrapperCell}>
              <DateCellRenderer value={utcDate} />
            </div>
          )
        },
        valueGetter: (params: any) => {
          const cellValue = params.data?.expectedDeparture;
          if (!cellValue) return null;
          try {
            const parsedDate = new Date(cellValue);
            const utcDate = new Date(parsedDate.getTime())
            return isNaN(utcDate.getTime()) ? null : utcDate;
          } catch (error) {
            console.error("Error parsing date:", cellValue, error);
            return null;
          }
        },
        filter: "agDateColumnFilter",
        filterParams: {
          maxNumConditions: 1,
          filterOptions: ['inRange'],
          comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (!cellValue) return -1;
            const cellDate = new Date(cellValue);
            const startOfDay = new Date(filterLocalDateAtMidnight)
            const endOfDay = new Date(filterLocalDateAtMidnight)
            endOfDay.setHours(23, 59, 59, 999);

            if (cellDate >= startOfDay && cellDate <= endOfDay) {
              return 0;
            }
            return cellDate < startOfDay ? -1 : 1;
          }
        }
      },
      {
        headerName: t("screens.trips.columnDef.unloadDate"),
        field: "expectedArrival",
        minWidth: 160,
        cellRenderer: (params: any) => {
          const cellValue = params.data?.expectedArrival;
          if (!cellValue) return null;
          const parsedDate = new Date(cellValue);
          const utcDate = new Date(parsedDate.getTime())
          return (
            <div style={agGridStyle.wrapperCell}>
              <DateCellRenderer value={utcDate} />
            </div>
          )
        },
        valueGetter: (params: any) => {
          const cellValue = params.data?.expectedArrival;
          if (!cellValue) return null;
          try {
            const parsedDate = new Date(cellValue);
            const utcDate = new Date(parsedDate.getTime())
            return isNaN(utcDate.getTime()) ? null : utcDate;
          } catch (error) {
            console.error("Error parsing date:", cellValue, error);
            return null;
          }
        },
        filter: "agDateColumnFilter",
        filterParams: {
          maxNumConditions: 1,
          filterOptions: ['inRange'],
          comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
            if (!cellValue) return -1;
            const cellDate = new Date(cellValue);
            const startOfDay = new Date(filterLocalDateAtMidnight)
            const endOfDay = new Date(filterLocalDateAtMidnight)
            endOfDay.setHours(23, 59, 59, 999);

            if (cellDate >= startOfDay && cellDate <= endOfDay) {
              return 0;
            }
            return cellDate < startOfDay ? -1 : 1;
          }
        }
      },
      
      {
        headerName: t("screens.trips.columnDef.tractorLicensePlate"),
        field: "tractorLicensePlate",
        minWidth: 185,
        filter: 'TractorPlateFilter',
        filterParams: {
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          const tripId = params.data.tripID;
          const approvalStatus = params.data.approval;
          const status = params.data.status;
          const plate = params.value;
          //const tractorPlateUpdatable = params.data.tractorPlateUpdatable;
          const tractorPlateUpdatable = true;

          console.log('Current driversMap:', driversMap);
          console.log('Looking for plate:', plate);
          console.log('Value in driversMap:', driversMap[plate]);
          const hasDriver = driversMap[plate] === true;
          console.log('Rendering cell for plate:', plate, 'hasDriver:', hasDriver);
          return (
            <div style={agGridStyle.wrapperCell}>
              <TractorLicensePlateCellRenderer
                value={plate}
                tripId={tripId}
                equipmentType="TRUCK"
                user={user?.email || "-"}
                onUpdate={(updatedValue) => {
                  params.setValue(updatedValue);
                }}
                approvalStatus={approvalStatus}
                vehiclePlateList={vehiclePlateList}
                status={status}
                hasDriver={hasDriver}
                tractorPlateUpdatable={tractorPlateUpdatable}
                
              />
            </div>
          );
        },
      },
      {
        headerName: t("screens.trips.columnDef.trailerLicensePlate"),
        field: "trailerLicensePlate",
        minWidth: 195,
        filter: 'TrailerPlateFilter',
        filterParams: {
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          const tripId = params.data.tripID;
          const approvalStatus = params.data.approval; 
          const status = params.data.status;
          //const trailerPlateUpdatable = params.data.trailerPlateUpdatable;
          const trailerPlateUpdatable =  true;

          return (
            <div style={agGridStyle.wrapperCell}>
              <TractorLicensePlateCellRenderer
                status={status}
                value={params.value}
                tripId={tripId}
                approvalStatus={approvalStatus}
                equipmentType="TRAILER"
                user={user?.email || "-"}
                onUpdate={(updatedValue) => {
                  params.setValue(updatedValue);
                }}
                trailerPlateList={trailerPlateList}
                trailerPlateUpdatable={trailerPlateUpdatable}
              />
            </div>
          );
        },
      },
      {
        headerName: t("screens.trips.columnDef.loadingLocation"),
        field: "loadingCity",
        minWidth: 240,
        resizable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          const loadCount = params.data.loadingCount;

          return (
            <CityNameCellRenderer value={params.value} loadCount={loadCount} />
          );
        },
      },
      {
        headerName: t("screens.trips.columnDef.unloadingLocation"),
        field: "unloadingCity",
        minWidth: 240,
        resizable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ['contains'],
          buttons: [],
          closeOnApply: false,
          suppressAndOrCondition: true,
        },
        floatingFilter: true,
        cellRenderer: (params: any) => {
          const unloadCount = params.data.unloadingCount;

          return (
            <CityNameCellRenderer value={params.value} unloadCount={unloadCount} />
          );
        },
      },
      {
        headerName: "",
        field: "documents",
        maxWidth: 100,
        floatingFilter: false,
        filter: false,
        sortable: false,
        cellRenderer: (params: any) => {
          const tripId = params.data.tripID;
          return (
            <DocumentsCellRenderer tripId={tripId} value={params.value} />
          );
        },
      },
    ],
    [t, navigate, setTripId, driversMap, user?.email, vehiclePlateList, trailerPlateList]
  );

  const defaultColDef: ColDef = {
    minWidth: 80,
    resizable: false,
    filter: true,
    floatingFilter: true,
    suppressFiltersToolPanel: false,
    sortable: true,
    flex: 1,
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPaginationPageSize(newPageSize);
  };
  const localeText = {
    page: t('screens.trips.agGrid.page'),
    nextPage: t('screens.trips.agGrid.nextPage'),
    previousPage: t('screens.trips.agGrid.previousPage'),
    lastPage: t('screens.trips.agGrid.lastPage'),
    firstPage: t('screens.trips.agGrid.firstPage'),
    ariaPaginationPage: t('screens.trips.agGrid.ariaPaginationPage'),
    ariaPagination: t('screens.trips.agGrid.ariaPagination'),
    ariaLabelPagination: t('screens.trips.agGrid.ariaLabelPagination'),
    rangeSelectionEmpty: t('screens.trips.agGrid.rangeSelectionEmpty'),
    paginationSummary: t('screens.trips.agGrid.paginationSummary'),
    empty: t('screens.trips.agGrid.empty'),
    loadingOoo: t('screens.trips.agGrid.loadingOoo'),
    to: t('screens.trips.agGrid.to'),
    of: t('screens.trips.agGrid.of')
  };

  const statusMap: Record<string, string> = {
    "ASSIGNED": t('screens.trips.statusOptions.ASSIGNED'),
    "ALLOCATED": t("screens.trips.statusOptions.ALLOCATED"),
    "IN PROGRESS": t("screens.trips.statusOptions.IN_PROGRESS"),
    "WAITING FOR DOCUMENTATION": t("screens.trips.statusOptions.WAITING_FOR_DOCUMENTATION"),
    "COMPLETED": t("screens.trips.statusOptions.COMPLETED"),
  };

  const approvalMap: Record<string, string> = {
    "APPROVED": t("screens.trips.approvalStatus.APPROVED"),
    "REJECTED": t("screens.trips.approvalStatus.REJECTED"),
    "TO APPROVE": t("screens.trips.approvalStatus.TO_APPROVE"),
    "AUTO ACCEPTED": t("screens.trips.approvalStatus.AUTO_ACCEPTED"),
    "TO BE EVALUATED": t("screens.trips.approvalStatus.TO_BE_EVALUATED"),
    "TO BE UPLOADED": t("screens.trips.approvalStatus.TO_BE_UPLOADED"),
    "ACCEPTED": t("screens.trips.approvalStatus.ACCEPTED"),
  }


  const reverseStatusMap = Object.fromEntries(
    Object.entries(statusMap).map(([en, it]) => [it, en])
  );

  const reverseApprovalMap = Object.fromEntries(
    Object.entries(approvalMap).map(([en, it]) => [it, en])
  );


  const onFilterChanged = (event: { api: any; }) => {
    const gridApi = event.api;
    const filterModel = gridApi.getFilterModel();

    if (filterModel.status) {
      filterModel.status.filter = reverseStatusMap[filterModel.status.filter] || filterModel.status.filter;
    }

    // Ensure the approval filter is properly applied
    if (filterModel.approval) {
      const approvalFilter = gridApi.getFilterInstance('approval');
      if (approvalFilter) {
        approvalFilter.setModel(filterModel.approval);
      }
    }

    // Ensure the tractor plate filter is properly applied
    if (filterModel.tractorLicensePlate) {
      const tractorPlateFilter = gridApi.getFilterInstance('tractorLicensePlate');
      if (tractorPlateFilter) {
        tractorPlateFilter.setModel(filterModel.tractorLicensePlate);
      }
    }

    // Ensure the trailer plate filter is properly applied
    if (filterModel.trailerLicensePlate) {
      const trailerPlateFilter = gridApi.getFilterInstance('trailerLicensePlate');
      if (trailerPlateFilter) {
        trailerPlateFilter.setModel(filterModel.trailerLicensePlate);
      }
    }

    gridApi.setFilterModel(filterModel);
  };


  return (
    <>
      <div
        className="ag-theme-alpine"
        style={{ height: "100%", width: "100%", overflowX: "auto" }}
      >
        {isLoading || (isLoadingDrivers && localTrips.length > 0) ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: "200px" }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <AgGridReact
              getRowId={(params) => params.data.tmsObjectNumber}
              ref={gridRef}
              rowData={localTrips}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={paginationPageSize}
              domLayout="autoHeight"
              localeText={localeText}
              onFilterChanged={onFilterChanged}
              frameworkComponents={{
                agCustomFilter: ApprovalFilter,
                StatusFilter: StatusFilter,
                TractorPlateFilter: TractorPlateFilter,
                TrailerPlateFilter: TrailerPlateFilter
              }}
              suppressPropertyNamesCheck={true}
            />
            <div style={{ width: "100%", height: "4px" }}>
              <PaginationSizeSelector
                currentPageSize={paginationPageSize}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default AgGridTrips;
